/* eslint-disable max-len */
import React, {type RefObject} from 'react';

/**
 * The `FileProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface FileProps {
    /**
     * A string representing the CSS class to be applied to the FileIcon element to override its default styling.
     *
     * @default ''
     */
    className?: string;
    /**
     * A string representing the color to be applied.
     *
     * @default '#282828'
     */
    color1?: string;
    /**
     * A number or string representing the height of the FileIcon element.
     *
     * @default 49
     */
    height?: number | string;
    /**
     * The `ref` property represents a reference to the SVG element, allowing for direct manipulation or access to the DOM node.
     */
    ref?: RefObject<SVGSVGElement | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'File'
     */
    testId?: string;
    /**
     * A number or string representing the height of the FileIcon element.
     *
     * @default 48
     */
    width?: number | string;
}

/**
 * The `File` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `FileProps` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props           The component props.
 * @param props.className A string representing the CSS class to be applied to the FileIcon element to override its default styling.
 * @param props.color1    A string representing the color to be applied to the Icon.
 * @param props.height    A number or string representing the height of the FileIcon element.
 * @param props.ref       A reference to the SVG element, allowing for direct manipulation or access to the DOM node.
 * @param props.testId    A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width     A number or string representing the height of the FileIcon element.
 * @returns A React element representing the `Check` component with the specified properties.
 *
 * @example
 * ```tsx
 * const FileComponent = <File className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const File = ({
    className = '',
    color1 = '#282828',
    height = 49,
    ref,
    testId = 'File',
    width = 48
}: FileProps) => (
    <svg
        ref={ref}
        className={className}
        data-cy={testId}
        height={height}
        viewBox="0 0 48 49"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
    >
        <path d="M27.53 4.31H11.72a3.95 3.95 0 0 0-3.96 3.96V39.9a3.95 3.95 0 0 0 3.96 3.95h23.72a3.95 3.95 0 0 0 3.96-3.95V16.18z" fill="transparent" stroke={color1} strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.59" />
        <path d="M27.51 5.01v11.05H39.3M31.44 26.1H15.72m15.72 8.02H15.72m3.93-16.06h-3.93" fill="transparent" stroke={color1} strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.59" />
    </svg>
);

File.displayName = 'File';